import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Tipos para diferentes tipos de parámetros
type ParamType = 'string' | 'number' | 'boolean' | 'json' | 'array';

interface ParamConfig<T = any> {
    type: ParamType;
    default: T;
    key?: string; // Si se quiere usar una key diferente en la URL
}

type ParamConfigMap = Record<string, ParamConfig>;
type ParamValues<T extends ParamConfigMap> = {
    [K in keyof T]: T[K]['default'];
};

/**
 * Hook para manejar query parameters de forma type-safe y reutilizable
 * 
 * @example
 * const { params, updateParam, updateParams } = useQueryParams({
 *   search: { type: 'string', default: '' },
 *   filters: { type: 'json', default: [] },
 *   sortBy: { type: 'string', default: 'created_at' },
 *   sortDirection: { type: 'string', default: 'desc' },
 *   page: { type: 'number', default: 1 }
 * });
 */
export function useQueryParams<T extends ParamConfigMap>(config: T) {
    const [searchParams, setSearchParams] = useSearchParams();
    // Estado inicial desde URL (mismo patrón que Orders/List)
    const [params, setParams] = useState<ParamValues<T>>(() =>
        parseParamsFromURL(config, searchParams)
    );

    // Función para parsear parámetros desde URL
    function parseParamsFromURL<T extends ParamConfigMap>(
        config: T,
        searchParams: URLSearchParams
    ): ParamValues<T> {
        const result = {} as ParamValues<T>;

        Object.entries(config).forEach(([key, paramConfig]) => {
            const urlKey = paramConfig.key || key;
            // date_from/date_to: aceptar también from/to en la URL (compatibilidad)
            let urlValue = searchParams.get(urlKey);
            if (urlValue === null && (key === 'date_from' || key === 'date_to')) {
                urlValue = searchParams.get(key === 'date_from' ? 'from' : 'to');
            }

            if (urlValue === null) {
                result[key as keyof T] = paramConfig.default;
                return;
            }

            try {
                switch (paramConfig.type) {
                    case 'string':
                        result[key as keyof T] = urlValue as any;
                        break;
                    case 'number':
                        result[key as keyof T] = (urlValue ? Number(urlValue) : paramConfig.default) as any;
                        break;
                    case 'boolean':
                        result[key as keyof T] = (urlValue === 'true') as any;
                        break;
                    case 'json':
                        // No usar decodeURIComponent porque URLSearchParams.get() ya devuelve el valor decodificado
                        result[key as keyof T] = (urlValue ? JSON.parse(urlValue) : paramConfig.default) as any;
                        break;
                    case 'array':
                        result[key as keyof T] = (urlValue ? urlValue.split(',') : paramConfig.default) as any;
                        break;
                    default:
                        result[key as keyof T] = paramConfig.default;
                }
            } catch (error) {
                console.warn(`Error parsing URL param ${key}:`, error);
                result[key as keyof T] = paramConfig.default;
            }
        });

        return result;
    }

    // Función para serializar parámetros a URL
    function serializeParamToURL(value: any, type: ParamType): string | null {
        if (value === null || value === undefined) {
            return null;
        }

        switch (type) {
            case 'string':
                return value.toString().trim() || null;
            case 'number':
                return value.toString();
            case 'boolean':
                return value.toString();
            case 'json':
                // No usar encodeURIComponent porque URLSearchParams.set() ya lo hace automáticamente
                return Array.isArray(value) && value.length === 0
                    ? null
                    : JSON.stringify(value);
            case 'array':
                return Array.isArray(value) && value.length > 0
                    ? value.join(',')
                    : null;
            default:
                return value.toString();
        }
    }

    // Sincronizar desde URL al montar (mismo patrón que Orders/List)
    useEffect(() => {
        const initialParams = parseParamsFromURL(config, searchParams);
        setParams(initialParams);
    }, []); // Solo al montar

    // 🎯 Sincronizar parámetros cuando cambia la URL (para detectar navegaciones a la misma ruta con diferentes query params)
    // Usamos searchParams.toString() como dependencia para detectar cualquier cambio en los query params
    useEffect(() => {
        const currentParams = parseParamsFromURL(config, searchParams);
        setParams(prevParams => {
            // Solo actualizar si realmente hay cambios para evitar re-renders innecesarios
            const hasChanges = Object.keys(config).some(key => {
                const urlKey = config[key].key || key;
                const currentValue = currentParams[key as keyof typeof currentParams];
                const prevValue = prevParams[key as keyof typeof prevParams];
                return currentValue !== prevValue;
            });
            
            return hasChanges ? currentParams : prevParams;
        });
    }, [searchParams.toString()]); // eslint-disable-line react-hooks/exhaustive-deps

    // Función para actualizar un solo parámetro
    const updateParam = useCallback(<K extends keyof T>(
        key: K,
        value: T[K]['default']
    ) => {
        const newParams = new URLSearchParams(searchParams);
        const paramConfig = config[key];
        const urlKey = paramConfig.key || key as string;

        const serializedValue = serializeParamToURL(value, paramConfig.type);

        if (serializedValue !== null) {
            newParams.set(urlKey, serializedValue);
        } else {
            newParams.delete(urlKey);
        }

        setSearchParams(newParams);
        setParams(prev => ({ ...prev, [key]: value }));
    }, [searchParams, setSearchParams, config]);

    // Función para actualizar múltiples parámetros
    const updateParams = useCallback((updates: Partial<ParamValues<T>>) => {
        const newParams = new URLSearchParams(searchParams);

        Object.entries(updates).forEach(([key, value]) => {
            const paramConfig = config[key];
            if (!paramConfig) return;

            const urlKey = paramConfig.key || key;
            const serializedValue = serializeParamToURL(value, paramConfig.type);

            if (serializedValue !== null) {
                newParams.set(urlKey, serializedValue);
            } else {
                newParams.delete(urlKey);
            }
        });

        setSearchParams(newParams);
        setParams(prev => ({ ...prev, ...updates }));
    }, [searchParams, setSearchParams, config]);

    // Función para resetear todos los parámetros
    const resetParams = useCallback(() => {
        const defaults = {} as ParamValues<T>;
        Object.entries(config).forEach(([key, paramConfig]) => {
            defaults[key as keyof T] = paramConfig.default;
        });

        setSearchParams(new URLSearchParams());
        setParams(defaults);
    }, [config, setSearchParams]);

    // Función para obtener el valor actual de un parámetro
    const getParam = useCallback(<K extends keyof T>(key: K): T[K]['default'] => {
        return params[key];
    }, [params]);

    return {
        params,
        updateParam,
        updateParams,
        resetParams,
        getParam,
        // Helpers para casos comunes
        hasParams: Object.values(params).some(value =>
            value !== null &&
            value !== undefined &&
            value !== '' &&
            !(Array.isArray(value) && value.length === 0)
        )
    };
} 